import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import MultiStepForm from "@/components/MultiStepForm";
import { createActivitySchema, FormValues } from "./validation/schema";
import ActivityDetailsStep from "./steps/ActivityDetailsStep";
import ActivityDatesStep from "./steps/ActivityDatesStep";
import ModulesStep from "./steps/ModulesStep";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { Path } from "@/router";
import { RateUnit } from "../../types";

interface ActivityFormProps {
  initialValues?: Partial<FormValues>;
  onSubmit: (data: FormValues) => Promise<void>;
  isEditing?: boolean;
  isSubmitting?: boolean;
  activityId?: string;
  errorBanner?: string;
  setErrorBanner?: (errorBanner: string | undefined) => void;
  rateUnit?: RateUnit;
  totalParticipations?: number;
}

export default function ActivityForm({
  initialValues,
  onSubmit,
  isEditing = false,
  isSubmitting = false,
  errorBanner,
  setErrorBanner,
  rateUnit,
  totalParticipations
}: ActivityFormProps) {
  const { activityTypeId } = useParams();

  // Set activity type based on the context
  const defaultActivityType = activityTypeId || "";

  // Get today's date in yyyy-MM-dd format
  const today = new Date().toISOString().split("T")[0];

  const defaultValues: FormValues = {
    title: "",
    activityType: { id: defaultActivityType, name: "" },
    sectionId: "",
    section: "",
    activityDescription: "",
    duration: "",
    startDate: { startDate: today, endDate: today },
    endDate: { startDate: "", endDate: "" },
    checkinEnabled: false
  };

  const form = useForm<FormValues>({
    defaultValues: { ...defaultValues, ...initialValues },
    resolver: zodResolver(createActivitySchema),
    mode: "onChange"
  });

  const steps = [
    {
      headline: "Activity Details",
      supportingText: "Step 1",
      content: (
        <ActivityDetailsStep isActivityTypeDisabled={!!defaultActivityType} totalParticipations={totalParticipations} />
      ),
      requiredFields: ["title", "activityType", "sectionId", "activityDescription"]
    },
    {
      headline: "Activity Dates",
      supportingText: "Step 2",
      content: <ActivityDatesStep rateUnit={rateUnit} />,
      requiredFields: rateUnit ? ["duration", "startDate"] : ["startDate"]
    },
    {
      headline: "Modules",
      supportingText: "Step 3",
      content: <ModulesStep />,
      requiredFields: []
    }
  ];

  return (
    <MultiStepForm
      form={form}
      steps={steps}
      onSubmit={onSubmit}
      validationSchema={createActivitySchema}
      renderStepper={true}
      backLink={{ to: ("/activities/activity-types/" + activityTypeId) as Path, title: "Cancel" }}
      submitText="Save Activity"
      customHeadline="Save Activity"
      submitDialogContent={<p>Are you sure you want to save this activity? </p>}
      modelName="Activity"
      isEditing={isEditing}
      isSubmitting={isSubmitting}
      errorBanner={errorBanner}
      setErrorBanner={setErrorBanner}
    />
  );
}
